// import { randomUUID as uuid } from 'crypto';

import type { useRouter } from "next/navigation";

import {
    checkIfPracujMobileApplicationSucceedExists,
    isPracujMobileTyPage,
    onPracujMobileApplicationSucceed,
} from "utils/helpers/pracujMobile";

const PageTypeValues = {
    Pracuj: "pracuj",
    TheProtocol: "theprotocol",
    ERecruiter: "erecruiter",
} as const;

type PageType = (typeof PageTypeValues)[keyof typeof PageTypeValues];
export const isThankYouPageUrlValid = (searchParams: URLSearchParams): boolean => {
    const thankYouPageUrl = searchParams.get("thankYouPageUrl");

    if (!thankYouPageUrl || isPracujMobileTyPage(thankYouPageUrl)) {
        return false;
    }

    const decodedThankYouPageUrl = decodeURIComponent(thankYouPageUrl);

    const validDomainsRegex =
        /.*((\.)|(\/))((pracuj\.pl)|(theprotocol\.it)|(theprotocol-beta\.it)|(system\.erecruiter\.pl)|(beta[-]system\.erecruiter\.pl))(($)|(\/.*$))/;

    return validDomainsRegex.test(decodedThankYouPageUrl);
};

export const checkPageType = (url: string): PageType | null => {
    const decodedUrl = decodeURIComponent(url);

    const patterns: { [key in PageType]: RegExp } = {
        [PageTypeValues.Pracuj]: /pracuj\.pl/i,
        [PageTypeValues.TheProtocol]: /(theprotocol\.it|theprotocol-beta\.it)/i,
        [PageTypeValues.ERecruiter]: /(system\.erecruiter\.pl|beta[-]system\.erecruiter\.pl)/i,
    };
    const matchedType =
        (Object.entries(patterns) as [PageType, RegExp][]).find(([, pattern]) => pattern.test(decodedUrl))?.[0] || null;

    return matchedType;
};

const handlePracujThankYouPageUrl = (searchParams: URLSearchParams): string => {
    const thankYouPageUrl = searchParams.get("thankYouPageUrl");

    const url = new URL(decodeURIComponent(thankYouPageUrl ?? ""));

    searchParams.forEach((value, key) => {
        url.searchParams.set(key, value);
    });

    url.searchParams.delete("thankYouPageUrl");

    return url.toString();
};

export const getRedirectToThankYouPageUrl = () => {
    const searchParams = new URLSearchParams(window.location.search);
    const thankYouPageUrl = searchParams.get("thankYouPageUrl");
    if (!thankYouPageUrl) {
        return "";
    }
    const pageType = checkPageType(thankYouPageUrl);

    if (pageType === PageTypeValues.Pracuj) {
        return handlePracujThankYouPageUrl(searchParams);
    }
    if (pageType === PageTypeValues.TheProtocol) {
        return decodeURIComponent(thankYouPageUrl);
    }

    return thankYouPageUrl;
};

export function redirectToNextStep({
    router,
    pathname,
    searchParams,
    shouldRenderSecondStep,
    id = window.crypto.randomUUID(),
    withCv,
}: {
    router: ReturnType<typeof useRouter>;
    pathname: string;
    searchParams: URLSearchParams;
    shouldRenderSecondStep: boolean;
    id?: string;
    withCv: boolean;
}) {
    const isExternalThankYouPageUrlExist = isThankYouPageUrlValid(searchParams);
    const thankYouUrl = isExternalThankYouPageUrlExist
        ? getRedirectToThankYouPageUrl()
        : `${pathname}/thank-you?${searchParams.toString()}`;

    const secondStepUrl = `${pathname}/second-step?${searchParams.toString()}&applyId=${id}&withCv=${withCv}`;

    if (shouldRenderSecondStep) {
        return router.replace(secondStepUrl);
    }

    if (checkIfPracujMobileApplicationSucceedExists()) {
        onPracujMobileApplicationSucceed(withCv);
    }

    return router.replace(thankYouUrl);
}
